import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import BlockContent from '@sanity/block-content-to-react';
import SanityImage from 'gatsby-plugin-sanity-image';
import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon } from '@heroicons/react/24/solid';
import { getIcon } from '../../utils';
import { AnchorText } from './AnchorText';

const CheckIcon = getIcon('Check');

const StyledCheckIcon = styled((props) => <CheckIcon {...props} />)`
  color: ${(props) => props.$color} !important;
  &:hover {
    color: ${(props) => props.$colorHover} !important;
  }
`;

const StyledDisclosureButton = styled((props) => (
  <Disclosure.Button {...props} />
))`
  background-color: ${(props) => props.$bgColor} !important;
  &:hover {
    background-color: ${(props) => props.$bgColorHover} !important;
  }
  & h3 {
    color: ${(props) => props.$color} !important;
    &:hover {
      color: ${(props) => props.$colorHover} !important;
    }
  }
`;

const StyledChevronUpIcon = styled((props) => <ChevronUpIcon {...props} />)`
  stroke: ${(props) => props.$color} !important;
  &:hover {
    stroke: ${(props) => props.$colorHover} !important;
  }
`;

const SanityBlockContent = (props) => {
  const { blocks, classes, sanity } = props;

  return (
    <div className={`space-y-6 ${classes}`}>
      <BlockContent
        blocks={blocks}
        serializers={Serializers(sanity)}
        className={`sanity-headlines space-y-6 ${classes}`}
      />
    </div>
  );
};

SanityBlockContent.defaultProps = {
  classes: '',
};

SanityBlockContent.propTypes = {
  blocks: PropTypes.array.isRequired,
  classes: PropTypes.string,
};

const Serializers = (sanity) => {
  const { colors } = sanity;

  // Here are your serializers

  return {
    types: {
      imageAlt: (props) => (
        <div className="pt-0">
          <SanityImage
            {...props?.node}
            alt={props?.node?.alt}
            loading="lazy"
            className={
              props?.node?.aspectRatio === 'default'
                ? `aspect-2 h-full w-full rounded-xl object-cover shadow-2xl transition hover:scale-105`
                : props?.node?.aspectRatio === 'none'
                ? 'h-full w-full transition hover:scale-105'
                : `${props?.node?.aspectRatio} h-full w-full rounded-xl object-cover shadow-2xl transition hover:scale-105`
            }
          />
        </div>
      ),
      accordion: (props) => (
        <div className="mx-auto w-full rounded-2xl p-2">
          <Disclosure>
            {({ open }) => (
              <div>
                <StyledDisclosureButton
                  $color={colors?.neutral?.white}
                  $colorHover={colors?.neutral?.white}
                  $bgColor={colors?.primary?.dark}
                  $bgColorHover={colors?.primary?.darker}
                  className="flex w-full justify-between rounded-lg px-4 py-2 text-left text-sm font-medium focus:outline-none focus-visible:ring focus-visible:ring-gray-500 focus-visible:ring-opacity-75"
                >
                  <h3 className="text-xl font-normal normal-case">
                    <span className="!text-white">{props.node.headline}</span>
                  </h3>
                  <StyledChevronUpIcon
                    $color={colors?.neutral?.white}
                    $colorHover={colors?.accent?.lighter}
                    className={`${
                      open ? 'rotate-180 transform' : ''
                    } mt-1 h-5 w-5 stroke-2`}
                  />
                </StyledDisclosureButton>
                <Disclosure.Panel className="px-4 pb-2 pt-4">
                  <SanityBlockContent
                    blocks={props.node.description}
                    sanity={sanity}
                  />
                </Disclosure.Panel>
              </div>
            )}
          </Disclosure>
        </div>
      ),
      // serviceAreas: (props) => (
      //   <div className="mx-auto w-full rounded-2xl p-2">
      //     <Disclosure>
      //       {({ open }) => (
      //         <div>
      //           <StyledDisclosureButton
      //             $color={colors?.neutral?.white}
      //             $colorHover={colors?.neutral?.white}
      //             $bgColor={primary.dark.color}
      //             $bgColorHover={colors?.primary?.darker}
      //             className="flex w-full justify-between rounded-lg px-4 py-2 text-left text-sm font-medium focus:outline-none focus-visible:ring focus-visible:ring-gray-500 focus-visible:ring-opacity-75"
      //           >
      //             <h3 className="text-xl font-normal normal-case">
      //               {props.node.headline}
      //             </h3>
      //             <StyledChevronUpIcon
      //               $color={colors?.neutral?.white}
      //               $colorHover={colors?.neutral?.white}
      //               className={`${
      //                 open ? 'rotate-180 transform' : ''
      //               } mt-1 h-5 w-5`}
      //             />
      //           </StyledDisclosureButton>
      //           <Disclosure.Panel className="px-4 pb-2 pt-4">
      //             <ul className="sanity-ul columns-1 text-left lg:columns-2">
      //               {locations.map((location) => (
      //                 <li key={location?.cityStateSlug}>
      //                   <AnchorText
      //                      sanity={sanity}
      //                     type="internal"
      //                     color={colors?.neutral?.default}
      //                     colorHover={colors?.accent?.default}
      //                     to={location?.cityStateSlug}
      //                     className="text-base"
      //                   >
      //                     {location?.cityStateZip}
      //                   </AnchorText>
      //                 </li>
      //               ))}
      //             </ul>
      //           </Disclosure.Panel>
      //         </div>
      //       )}
      //     </Disclosure>
      //   </div>
      // ),
    },
    styles: (props) => {
      const { type } = props;
      const quote = type === 'blockquote';
      if (quote) {
        return (
          <blockquote className="border border-l-2 border-gray-500">
            {props.children}
          </blockquote>
        );
      }
    },
    list: (props) => {
      const { type } = props;
      const bullet = type === 'bullet';
      const number = type === 'number';
      const check = type === 'check';
      const checkCols = type === 'checkCols';

      if (bullet) {
        return (
          <ul className="sanity-ul space-y-2 text-left">{props.children}</ul>
        );
      } else if (number) {
        return (
          <ol className="sanity-ol space-y-2 text-left">{props.children}</ol>
        );
      } else if (check) {
        return <ul className="space-y-2 text-left">{props.children}</ul>;
      } else if (checkCols) {
        return (
          <ul className="columns-1 space-y-2 text-left md:columns-2">
            {props.children}
          </ul>
        );
      } else {
        return (
          <ul className="sanity-ul space-y-2 text-left">{props.children}</ul>
        );
      }
    },
    listItem: (props) => {
      const { node } = props;
      const check = node.listItem === 'check' || node.listItem === 'checkCols';
      if (check) {
        return (
          <li className="ml-4 flex gap-x-4">
            <span className="mt-2">
              <StyledCheckIcon
                $color={colors?.accent?.default}
                $colorHover={colors?.accent?.default}
              />
            </span>
            <div>{props.children}</div>
          </li>
        );
      } else {
        return <li className="">{props.children}</li>;
      }
    },
    marks: {
      columns: ({ children }) => <span className="!columns-2">{children}</span>,
      highlight: ({ children }) => (
        <span className="bg-yellow-400 py-1 font-bold">{children}</span>
      ),
      blockMobile: ({ children }) => (
        <span className="block flex-row lg:inline">{children}</span>
      ),
      alignLeft: ({ children }) => (
        <span className="text-left">{children}</span>
      ),
      alignRight: ({ children }) => (
        <span className="text-right">{children}</span>
      ),
      alignCenter: ({ children }) => (
        <span className="!lg:text-center !block !text-center">{children}</span>
      ),
      alignJustify: ({ children }) => (
        <span className="text-justify">{children}</span>
      ),
      alignLeftMobile: ({ children }) => (
        <span className="block text-left lg:text-left">{children}</span>
      ),
      alignRightMobile: ({ children }) => (
        <span className="block text-right lg:text-left">{children}</span>
      ),
      alignCenterMobile: ({ children }) => (
        <span className="block text-center lg:text-left">{children}</span>
      ),
      alignJustifyMobile: ({ children }) => (
        <span className="block text-justify lg:text-left">{children}</span>
      ),
      textXs: ({ children }) => (
        <span className="text-xs lg:text-xs">{children}</span>
      ),
      textSm: ({ children }) => (
        <span className="text-sm lg:text-sm">{children}</span>
      ),
      textMd: ({ children }) => (
        <span className="text-base lg:text-base">{children}</span>
      ),
      textLg: ({ children }) => (
        <span className="text-lg lg:text-lg">{children}</span>
      ),
      textXl: ({ children }) => (
        <span className="text-xl lg:text-xl">{children}</span>
      ),
      text2xl: ({ children }) => (
        <span className="text-xl lg:text-2xl">{children}</span>
      ),
      text3xl: ({ children }) => (
        <span className="text-2xl lg:text-3xl">{children}</span>
      ),
      text4xl: ({ children }) => (
        <span className="text-2xl lg:text-4xl">{children}</span>
      ),
      text5xl: ({ children }) => (
        <span className="text-2xl lg:text-5xl">{children}</span>
      ),
      text6xl: ({ children }) => (
        <span className="text-2xl lg:text-6xl">{children}</span>
      ),
      text7xl: ({ children }) => (
        <span className="text-2xl lg:text-7xl">{children}</span>
      ),
      text8xl: ({ children }) => (
        <span className="text-3xl lg:text-8xl">{children}</span>
      ),
      text9xl: ({ children }) => (
        <span className="text-3xl lg:text-9xl">{children}</span>
      ),
      internalLink: ({ mark, children }) => {
        const { reference = {} } = mark;
        const href = reference?.slug?.current;
        return (
          <AnchorText
            sanity={sanity}
            type="internal"
            to={href}
            color={colors?.accent?.default}
            colorHover={colors?.accent?.dark}
          >
            {children}
          </AnchorText>
        );
      },
      externalLink: ({ mark, children }) => {
        const { blank, href } = mark;
        return blank ? (
          <AnchorText
            sanity={sanity}
            type="external"
            href={href}
            color={colors?.accent?.default}
            colorHover={colors?.accent?.dark}
          >
            {children}
          </AnchorText>
        ) : (
          <a href={href}>{children}</a>
        );
      },
    },
  };
};

export { SanityBlockContent };
